import type {
  WorkflowRunSummary,
  WorkflowRunCost,
} from '@cotera/client/app/etc/api/typed-client';
import {
  WORKFLOW_CRON_TEST_TRIGGER_TYPE,
  WORKFLOW_DATASET_TRIGGER_TYPE,
  WORKFLOW_DRAFT_TEST_TRIGGER_TYPE,
  WORKFLOW_WEBHOOK_TEST_TRIGGER_TYPE,
} from './workflow-run-status';

export type {
  WorkflowRunGraph,
  WorkflowRunGraphNode,
  WorkflowRunGraphEdge,
  WorkflowRunGraphInstanceSummary,
  WorkflowRunStepPayload,
  WorkflowRunStepInstancePayload,
  WorkflowRunCost,
} from '@cotera/client/app/etc/api/typed-client';

/** A single workflow run with timestamp fields normalized to `Date`. */
export type WorkflowRun = {
  id: string;
  workflowId: string;
  /** The attempt this run replays; null for a first attempt. */
  retryOf: string | null;
  /** 0-based attempt index within the retry chain (original = 0). */
  retryNum: number;
  triggerType: string;
  triggerId: string;
  status: string;
  isTest: boolean;
  error: string | null;
  startedAt: Date | null;
  finishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  /**
   * The most items any single step processed — 1 for a linear run, N for a
   * for-each over N items. Null when the server did not report it.
   */
  itemsProcessed: number | null;
  /**
   * The resolved idempotency key the run was dispatched under, from the
   * workflow's user-configured deduplication setting. Null when there is none —
   * including dataset-triggered runs, whose stored key is a system-generated
   * `dataset|workflow|row|evaluation` id rather than anything the user chose.
   */
  idempotencyKey: string | null;
};

/** A terminal (leaf) node's final output. */
export type WorkflowRunOutput = {
  nodeId: string;
  name: string;
  output: unknown;
};

/** A single run plus its hydrated trigger input and final outputs. */
export type WorkflowRunDetail = WorkflowRun & {
  /**
   * A newer attempt that supersedes this one; null when this is the latest.
   * Only superseded runs can be reached with this set — they are hidden from the
   * runs list, so the detail page is the only way in.
   */
  retriedByRunId: string | null;
  input: unknown | null;
  outputs: WorkflowRunOutput[];
  cost: WorkflowRunCost;
};

/** Zero-cost fallback for runs that predate cost tracking or hit an error. */
export const EMPTY_WORKFLOW_RUN_COST: WorkflowRunCost = {
  credits: 0,
  breakdown: { llm: 0, toolcall: 0 },
};

export const toDate = (value: Date | string | null): Date | null =>
  value === null ? null : value instanceof Date ? value : new Date(value);

/** Normalizes a `WorkflowRunSummary` API row into a `WorkflowRun`. */
export const normalizeRun = (r: WorkflowRunSummary): WorkflowRun => ({
  id: r.id,
  workflowId: r.workflowId,
  retryOf: r.retryOf ?? null,
  retryNum: r.retryNum ?? 0,
  triggerType: r.triggerType,
  triggerId: r.triggerId,
  status: r.status,
  isTest:
    r.triggerType === WORKFLOW_WEBHOOK_TEST_TRIGGER_TYPE ||
    r.triggerType === WORKFLOW_CRON_TEST_TRIGGER_TYPE ||
    r.triggerType === WORKFLOW_DRAFT_TEST_TRIGGER_TYPE,
  error: r.error,
  startedAt: toDate(r.startedAt),
  finishedAt: toDate(r.finishedAt),
  createdAt: toDate(r.createdAt) ?? new Date(0),
  updatedAt: toDate(r.updatedAt) ?? new Date(0),
  itemsProcessed: r.itemsProcessed ?? null,
  idempotencyKey:
    r.triggerType === WORKFLOW_DATASET_TRIGGER_TYPE
      ? null
      : r.idempotencyKey ?? null,
});
